import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, CalendarDays, ChevronRight, Clock, Facebook, Home, Linkedin, Link2, Loader2,
  MessageCircle, User,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { articleImg, ArticleCard } from "./NewsHub";
import api, { API_BASE } from "../lib/api";

const focal = (a) => ({ objectPosition: `${a.focal_x ?? 50}% ${a.focal_y ?? 50}%` });

function ContentBlock({ text }) {
  // simple rich text: ## heading, - bullet, 1. numbered, > quote, ![alt](path|caption), [video](url), **bold**
  return (
    <div className="space-y-4 text-[15px] leading-relaxed text-slate-700" data-testid="article-content">
      {(text || "").split(/\n{2,}/).map((block, i) => {
        const b = block.trim();
        if (!b) return null;
        if (b.startsWith("## ")) return <h2 key={i} className="pt-4 font-heading text-xl font-extrabold text-navy">{b.slice(3)}</h2>;
        if (b.startsWith("### ")) return <h3 key={i} className="pt-3 font-heading text-lg font-bold text-navy">{b.slice(4)}</h3>;
        if (b.startsWith("> ")) return (
          <blockquote key={i} className="border-l-4 border-saffron bg-saffron/5 px-5 py-3 font-medium italic text-slate-600">{b.slice(2)}</blockquote>
        );
        const imgM = b.match(/^!\[(.*?)\]\((.*?)\)(?:\|(.*))?$/);
        if (imgM) return (
          <figure key={i} className="overflow-hidden rounded-2xl">
            <img src={`${API_BASE}/api/files/${imgM[2]}`} alt={imgM[1]} loading="lazy" className="w-full object-cover" />
            {(imgM[3] || imgM[1]) && <figcaption className="mt-2 text-center text-xs text-slate-400">{imgM[3] || imgM[1]}</figcaption>}
          </figure>
        );
        const vidM = b.match(/^\[video\]\((.*?)\)$/);
        if (vidM) {
          const yt = vidM[1].match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
          if (yt) return (
            <div key={i} className="aspect-video overflow-hidden rounded-2xl">
              <iframe className="h-full w-full" src={`https://www.youtube.com/embed/${yt[1]}?rel=0`} title="Video" allow="encrypted-media; picture-in-picture" allowFullScreen />
            </div>
          );
          return <video key={i} controls playsInline preload="metadata" className="w-full rounded-2xl" src={`${API_BASE}/api/files/${vidM[1]}`} />;
        }
        if (b.split("\n").every((l) => /^-\s/.test(l.trim()))) return (
          <ul key={i} className="space-y-2">
            {b.split("\n").map((l, j) => <li key={j} className="flex gap-2.5"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-saffron" />{l.trim().slice(2)}</li>)}
          </ul>
        );
        if (b.split("\n").every((l) => /^\d+\.\s/.test(l.trim()))) return (
          <ol key={i} className="space-y-2">
            {b.split("\n").map((l, j) => <li key={j} className="flex gap-2.5"><span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-saffron/10 text-[10px] font-extrabold text-saffron">{j + 1}</span>{l.trim().replace(/^\d+\.\s/, "")}</li>)}
          </ol>
        );
        return <p key={i}>{b.split(/(\*\*[^*]+\*\*)/).map((seg, j) => seg.startsWith("**") ? <strong key={j} className="font-bold text-navy">{seg.slice(2, -2)}</strong> : seg)}</p>;
      })}
    </div>
  );
}

export default function NewsArticle() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [contact, setContact] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setData(null); setMissing(false);
    api.get(`/news/public/article/${slug}`).then((r) => setData(r.data)).catch(() => setMissing(true));
    api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {});
    window.scrollTo(0, 0);
  }, [slug]);

  const a = data?.article;
  useEffect(() => {
    if (!a) return;
    document.title = a.seo_title || `${a.title} | IndiaFast News`;
    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("name", "description", a.meta_description || a.excerpt || a.title);
    setMeta("property", "og:title", a.seo_title || a.title);
    setMeta("property", "og:description", a.meta_description || a.excerpt || a.title);
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "article-jsonld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "Article",
      headline: a.title, description: a.excerpt || a.title,
      author: { "@type": "Organization", name: a.author || "IndiaFast Global Couriers" },
      datePublished: (a.published_date || a.created_at || "").slice(0, 10),
      ...(a.updated_date ? { dateModified: a.updated_date } : {}),
      publisher: { "@type": "Organization", name: "IndiaFast Global Couriers" },
      mainEntityOfPage: `https://www.indiafast.in/p/news/${a.slug}`,
    });
    document.head.appendChild(ld);
    return () => document.getElementById("article-jsonld")?.remove();
  }, [a]);

  if (missing) return (
    <div className="grid min-h-screen place-items-center bg-slate-50" data-testid="article-404">
      <div className="text-center">
        <p className="font-heading text-2xl font-bold text-navy">Article not found</p>
        <Link to="/p/news" className="mt-4 inline-block rounded-full bg-saffron px-6 py-2.5 text-sm font-bold text-white">Back to News</Link>
      </div>
    </div>
  );
  if (!a) return <div className="grid min-h-screen place-items-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>;

  const url = `${window.location.origin}/p/news/${a.slug}`;
  const shareText = encodeURIComponent(a.title);
  const shareUrl = encodeURIComponent(url);
  const shares = [
    { Icon: MessageCircle, label: "WhatsApp", href: `https://wa.me/?text=${shareText}%20${shareUrl}`, cls: "bg-emerald-600" },
    { Icon: Facebook, label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`, cls: "bg-blue-600" },
    { Icon: Linkedin, label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`, cls: "bg-sky-700" },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="article-page">
      <Navbar />

      {/* ARTICLE HEADER */}
      <section className="relative overflow-hidden bg-navy pb-16 pt-32 sm:pt-36" data-testid="article-header">
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6">
          <nav className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400" data-testid="article-breadcrumb">
            <Link to="/" className="hover:text-saffron">Home</Link><ChevronRight className="h-3 w-3" />
            <Link to="/p/news" className="hover:text-saffron">News</Link><ChevronRight className="h-3 w-3" />
            <span className="line-clamp-1 text-saffron">{a.title}</span>
          </nav>
          <span className="mt-5 inline-block rounded-full bg-saffron/15 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-saffron">{a.category}</span>
          <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="article-title">{a.title}</h1>
          {a.excerpt && <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">{a.excerpt}</p>}
          <p className="mt-5 flex flex-wrap items-center gap-x-4 text-[11px] font-semibold text-slate-400" data-testid="article-meta">
            <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-saffron" />{a.author}</span>
            <span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-saffron" />{a.published_date || (a.created_at || "").slice(0, 10)}</span>
            {a.updated_date && <span>Updated {a.updated_date}</span>}
            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-saffron" />{a.reading_time} min read</span>
          </p>
        </div>
      </section>

      {/* HERO IMAGE */}
      {articleImg(a, "card") && (
        <div className="mx-auto -mt-2 max-w-4xl px-4 sm:px-6">
          <img src={articleImg(a, "card")} alt={a.title} className="mt-6 aspect-[16/9] w-full rounded-3xl object-cover shadow-xl" style={focal(a)} data-testid="article-hero-img" />
        </div>
      )}

      {/* BODY + SIDEBAR */}
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:grid lg:grid-cols-3 lg:gap-10">
        <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10 lg:col-span-2">
          <ContentBlock text={a.content} />
          {!!(a.tags || []).length && (
            <div className="mt-8 flex flex-wrap gap-2 border-t border-slate-100 pt-5">
              {a.tags.map((t) => <span key={t} className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-600">#{t}</span>)}
            </div>
          )}
        </article>

        <aside className="mt-10 space-y-6 lg:mt-0">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="article-share">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Share this story</p>
            <div className="mt-3 flex gap-2">
              {shares.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noreferrer" data-testid={`share-${s.label.toLowerCase()}`} aria-label={s.label}
                   className={`grid h-10 w-10 place-items-center rounded-full text-white transition-transform hover:-translate-y-0.5 ${s.cls}`}>
                  <s.Icon className="h-4 w-4" />
                </a>
              ))}
              <button onClick={async () => { try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.success(`Copy this link: ${url}`); } }} data-testid="share-copy" aria-label="Copy link"
                      className="grid h-10 w-10 place-items-center rounded-full bg-navy text-white transition-transform hover:-translate-y-0.5">
                <Link2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          {!!data.related.length && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:hidden" data-testid="article-related-mobile">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">You may also like</p>
              <div className="mt-3 space-y-2">
                {data.related.slice(0, 3).map((r) => (
                  <Link key={r.id} to={`/p/news/${r.slug}`} className="block text-sm font-bold text-navy hover:text-saffron">{r.title}</Link>
                ))}
              </div>
            </div>
          )}
          <div className="rounded-2xl bg-navy p-5" data-testid="article-cta">
            <p className="font-heading text-base font-bold text-white">Ready to ship?</p>
            <p className="mt-1 text-xs text-slate-300">Get live rates across our courier network.</p>
            <Link to="/rate-calculator" className="mt-4 block rounded-full bg-saffron py-2.5 text-center text-xs font-bold text-white hover:bg-saffron-hover">Get a Quote</Link>
          </div>
        </aside>
      </div>

      {/* RELATED */}
      {!!data.related.length && (
        <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6" data-testid="article-related">
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-navy">You May Also Like</h2>
          <div className="mt-7 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {data.related.map((r) => <ArticleCard key={r.id} a={r} />)}
          </div>
          <Link to="/p/news" data-testid="article-back" className="mt-9 inline-flex items-center gap-2 text-sm font-bold text-saffron hover:text-saffron-hover">
            <ArrowLeft className="h-4 w-4" /> Back to News &amp; Insights
          </Link>
        </section>
      )}

      <Footer contact={contact} />
    </div>
  );
}
